/**
 * <s-cta-fx>: site-wide hover interaction for call-to-action buttons ([data-cta]).
 * Placed once in the layout; listens on the document.
 * - Magnetic: the button leans a few pixels toward the pointer and springs back on leave.
 * - Light: a soft highlight follows the pointer inside the button (--cx / --cy).
 * - Text roll: on first hover the label's text is wrapped so it can roll up and a
 *   copy (aria-hidden) rolls in from below; icons are left untouched.
 * Mouse only; off with reduced motion.
 */
class SCtaFx extends HTMLElement {
  #active = null;
  #frame = 0;
  #event = null;

  #onOver = (event) => {
    if (event.pointerType !== 'mouse') return;
    const cta = event.target.closest?.('[data-cta]');
    if (!cta || cta === this.#active) return;
    this.#release();
    this.#active = cta;
    this.#prepare(cta);
    cta.setAttribute('data-cta-hover', '');
  };
  #onMove = (event) => {
    if (!this.#active || event.pointerType !== 'mouse') return;
    this.#event = event;
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#tick);
  };
  #onOut = (event) => {
    if (!this.#active) return;
    if (this.#active.contains(event.relatedTarget)) return;
    this.#release();
  };
  #tick = () => {
    this.#frame = 0;
    const cta = this.#active;
    const event = this.#event;
    if (!cta || !event) return;
    const box = cta.getBoundingClientRect();
    const px = (event.clientX - box.left) / box.width;
    const py = (event.clientY - box.top) / box.height;
    const pull = Math.min(8, box.width * 0.04);
    cta.style.setProperty('--mx', `${((px - 0.5) * 2 * pull).toFixed(2)}px`);
    cta.style.setProperty('--my', `${((py - 0.5) * 2 * pull * 0.6).toFixed(2)}px`);
    cta.style.setProperty('--cx', `${(px * 100).toFixed(1)}%`);
    cta.style.setProperty('--cy', `${(py * 100).toFixed(1)}%`);
  };

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.documentElement.setAttribute('data-cta-fx', '');
    document.addEventListener('pointerover', this.#onOver, { passive: true });
    document.addEventListener('pointermove', this.#onMove, { passive: true });
    document.addEventListener('pointerout', this.#onOut, { passive: true });
  }

  disconnectedCallback() {
    document.removeEventListener('pointerover', this.#onOver);
    document.removeEventListener('pointermove', this.#onMove);
    document.removeEventListener('pointerout', this.#onOut);
    cancelAnimationFrame(this.#frame);
  }

  #release() {
    const cta = this.#active;
    this.#active = null;
    if (!cta) return;
    cta.removeAttribute('data-cta-hover');
    cta.style.removeProperty('--mx');
    cta.style.removeProperty('--my');
  }

  /** Wrap each non-empty direct text node in a roll: visible copy + hidden duplicate below. */
  #prepare(cta) {
    if (cta.hasAttribute('data-cta-ready')) return;
    cta.setAttribute('data-cta-ready', '');
    for (const node of [...cta.childNodes]) {
      if (node.nodeType !== Node.TEXT_NODE || !node.textContent.trim()) continue;
      const text = node.textContent.trim();
      const roll = document.createElement('span');
      roll.className = 'cta-roll';
      const front = document.createElement('span');
      front.textContent = text;
      const back = document.createElement('span');
      back.textContent = text;
      back.setAttribute('aria-hidden', 'true');
      roll.append(front, back);
      node.replaceWith(roll);
    }
  }
}

if (!customElements.get('s-cta-fx')) customElements.define('s-cta-fx', SCtaFx);
